import { Component, Input } from '@angular/core';
import { isEmptyArray, isEmptyObject } from '@common/helpers';
import {
  PASSENGER_TYPE_LABEL,
  POS_COUNTRY_CODE,
} from '@common/helpers/constants/my-booking/my_booking_itinerary.contant';
import { PassengerType } from '@common/interfaces';
import { MilesFormatPipe } from '@common/pipes/miles-format/miles-format.pipe';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { SupportComponent } from '@lib/components/support-class';
import { MOffice } from '@lib/interfaces';
import { StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { Subscription } from 'rxjs';
import {
  GetOrderResponseData,
  GetOrderResponseDataOrderType,
  GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner,
} from 'src/sdk-servicing';

@Component({
  selector: 'asw-payment-detail-s01-p031',
  templateUrl: './payment-detail-s01-p031.component.html',
  styleUrls: ['./payment-detail-s01-p031.component.scss'],
})
export class PaymentDetailS01P031Component extends SupportComponent {
  @Input() data!: GetOrderResponseData;

  private _mOffice: Array<MOffice> = [];
  private _subscriptions: Subscription = new Subscription();
  currencyCodeByLang: string = 'JPY';

  constructor(
    private _common: CommonLibService,
    public _staticMsg: StaticMsgPipe,
    public _milesFormatPipe: MilesFormatPipe,
    private _myBookingItineraryService: MyBookingItineraryService
  ) {
    super(_common);
  }

  reload(): void {}

  init() {
    this._subscriptions.add(
      this._myBookingItineraryService.masterData$.subscribe((master) => {
        this._mOffice = master.mOffice ?? [];
      })
    );
    this.currencyCodeByLang = this._common.aswContextStoreService.aswContextData.currencyCode;
  }
  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }

  getClassModifiedElement(areThereChanges: boolean) {
    return areThereChanges ? 'c-modified-element' : '';
  }

  getStylingModifiedElementText(areThereChanges: boolean) {
    return areThereChanges ? { color: 'red' } : {};
  }

  /** 旅行者を数える */
  get isScrollRight(): boolean {
    return Number(this.data?.travelers?.length) > 1;
  }

  /** [1.9.4] 必要マイル: PNR情報取得API応答.data.orderType.isAwardBooking = true (特典PNR)の場合 */
  get isRequiredMiles(): boolean {
    return Boolean(this.data?.orderType?.isAwardBooking);
  }

  /** [1.9.4] 必要マイル */
  get requiredMiles(): number {
    /**「PNR情報取得API.data.prices.totalPrices.requiredMileage + " " + label.mileUnit」を表示する。*/
    return this.data?.prices?.totalPrices?.requiredMileage ?? 0;
  }

  /** [1.9.5] 支払総額ラベル */
  get totalPaymentLabel(): string {
    return this._myBookingItineraryService.getPaymentTotalLabel(this.data);
  }

  /** [1.9.6] 支払総額 */
  get getTotalPrices(): Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner> {
    return (
      this._myBookingItineraryService.getTotalPrices(this.data) ??
      ([] as Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner>)
    );
  }

  /** [1.9.6] 支払総額 */
  get isDifferentTotalPrices(): boolean {
    // ※PNR情報取得API応答.data.prices.totalPrices.isAirPriceChangeRepricing=true(運賃再計算の支払総額情報の変更有)の場合、支払総額を赤色で表示する。また、読み上げ用ラベルを先頭に追加する、差分が発生している旨の文言をラベルの前に設置する。
    return this.data?.prices?.totalPrices?.isAirPriceChangeRepricing === true;
  }

  /** [1.9.7] 搭乗者人数 */
  get getPassengerTypeCounts(): string {
    const countPassengerTypes = this._myBookingItineraryService.countPassengerTypes(this.data?.travelers);
    const labels: Array<string> = [];

    countPassengerTypes.forEach((count, passengerTypeCode) => {
      switch (passengerTypeCode) {
        /** 1.PNR情報取得API応答.data.travelers.passengerTypeCode="ADT"(大人)の場合、大人人数 + 大人を示す文言を表示する。*/
        case PassengerType.ADT:
          labels.push(`${count} ${this._staticMsg.transform(PASSENGER_TYPE_LABEL.ADULT)}`);
          break;

        /** 2.PNR情報取得API応答.data.travelers.passengerTypeCode="B15"(ヤングアダルト)の場合、ヤングアダルト人数 + ヤングアダルトを示す文言を表示する。*/
        case PassengerType.B15:
          labels.push(`${count} ${this._staticMsg.transform(PASSENGER_TYPE_LABEL.YOUNG_ADULT)}`);
          break;

        /** 3.PNR情報取得API応答.data.travelers.passengerTypeCode="CHD"(小児)の場合、小児人数 + 小児を示す文言を表示する。*/
        case PassengerType.CHD:
          labels.push(`${count} ${this._staticMsg.transform(PASSENGER_TYPE_LABEL.CHILD)}`);
          break;

        /** 4.PNR情報取得API応答.data.travelers.passengerTypeCode="INS"(席あり幼児)の場合、席あり幼児人数 + 席あり幼児を示す文言を表示する。*/
        case PassengerType.INF:
          labels.push(`${count} ${this._staticMsg.transform(PASSENGER_TYPE_LABEL.INFANT)}`);
          break;

        /** 5.PNR情報取得API応答.data.travelers.passengerTypeCode="INF"(席なし幼児)の場合、席なし幼児人数 + 席なし幼児を示す文言を表示する。*/
        case PassengerType.INS:
          labels.push(`${count} ${this._staticMsg.transform(PASSENGER_TYPE_LABEL.INS)}`);
          break;
      }
    });

    // Add comma between each content
    return labels.join(this._staticMsg.transform('label.commaSeparater'));
  }

  /** [1.9.11] 航空券金額情報 */
  get isAirCouponAmountInformation(): boolean {
    /** 支払情報サマリの同項目と同じ内容を表示する。 */
    return this._myBookingItineraryService.showTicketTotalPrices(this.data);
  }

  /** [1.9.12] 運賃総額 (航空券金額情報) */
  get fareTotalPrices(): Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner> {
    return this._myBookingItineraryService.getServicePricesBase(this.data);
  }

  /** [1.9.12] 運賃総額 (航空券金額情報) */
  get showFareTotalPrices(): boolean {
    return this._myBookingItineraryService.showFareTotalPrices(this.data);
  }

  /** [1.9.15] 税金総額 (航空券金額情報) */
  get taxTotalPrices(): Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner> {
    /** 支払情報サマリの同項目と同じ内容を表示する。 */
    const current_office = this._mOffice?.[0] ?? ({} as MOffice);
    return this._myBookingItineraryService.getTaxTotalPrices(this.data, current_office);
  }

  /** [1.9.15] 税金総額 (航空券金額情報) */
  get getLabelTaxTotalPrices(): string {
    /** 支払情報サマリの同項目と同じ内容を表示する。 */
    const current_office = this._mOffice?.[0] ?? ({} as MOffice);
    return this._myBookingItineraryService.getLabelTaxTotalPrices(current_office);
  }

  /** [1.9.16] 発行手数料、交換発行手数料総額 (航空券金額情報) */
  get ticketFeeTotalPrices(): Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner> {
    /** 支払情報サマリの同項目と同じ内容を表示する。 */
    return this._myBookingItineraryService.ticketPricesTotalFee(this.data);
  }

  /** [1.9.16] 発券手数料、交換発行手数料総 (航空券金額情報) */
  get showTicketFeeTotalPrices(): boolean {
    /** 支払情報サマリの同項目と同じ内容を表示する。 */
    const mOffice = this._mOffice?.[0] ?? ({} as MOffice);
    return this._myBookingItineraryService.showTicketFeeTotalPrices(mOffice, this.data);
  }
}
